// src/utils/elementHighlights.js
//
// View-only "element recognition" for the editor. Wherever the manuscript names
// one of the draft's story elements — a scene, actor or artifact on the Story
// Elements board — that name is tinted in its type's colour, and hovering it
// shows the element's description (components/editor/ElementHoverCard.jsx).
//
// Recognition rule, taken from the prototype: an element is recognized by its
// NAME, matched as a whole word and case-insensitively, longest name first, at
// every occurrence. "New Grail" therefore wins over "Grail" in "the New Grail",
// "craig" counts as Craig, and "Craigslist" does not. Nothing is marked by the
// author and nothing is stored: the highlight is derived from the text and the
// board each time it runs, so renaming or deleting an element updates the
// manuscript on the next pass with no clean-up of its own.
//
// Two deliberate differences from the prototype's `\b(name)\b`:
//   - The boundary is any letter or digit in any script, not JS's ASCII `\b`,
//     so "Zoë" is found in "Zoë's" and "Rosé" is not found inside "Roséwood".
//     For ASCII names the result is identical.
//   - Any run of whitespace in a name matches any run in the text, because
//     contentEditable litters text with &nbsp; and "New Grail" must still find
//     "New&nbsp;Grail".
//
// Safety model (identical to commentHighlights.js):
//   - Wrappers are tagged with HIGHLIGHT_ATTR so getCleanHTML() unwraps them
//     losslessly — highlights are NEVER persisted, broadcast or printed.
//   - The caret's own block IS decorated, as commentHighlights does: the caret
//     is recorded as a character offset first and put back afterwards. Unlike
//     there, it is only put back when that block actually changed, because this
//     layer is always on and runs after every pause in typing.
//   - Callers must CLEAR highlights before any structural DOM op (pagination
//     splits text nodes), then re-apply afterwards.
//   - Matching runs per BLOCK over the joined text of its nodes, because the
//     formatting toolbar splits a paragraph into one <span> per character. A
//     name spanning several nodes gets one wrapper per node; the stylesheet
//     keeps them flat so they read as one.

import { HIGHLIGHT_ATTR } from './editorSerialize';
import { caretOffsetIn, restoreCaretIn } from './caretPreserve';

const KIND = 'element';
// Which board node a wrapper stands for. Read by the hover card.
export const ELEMENT_ID_ATTR = 'data-rv-element-id';
// Which occurrence a wrapper belongs to, so the hover card can treat the
// several wrappers of one split name as a single word.
export const ELEMENT_MATCH_ATTR = 'data-rv-element-match';

const HL_CLASS = 'rv-element-highlight';
const TYPES = new Set(['scene', 'actor', 'artifact']);
const SELECTOR = `[${HIGHLIGHT_ATTR}="${KIND}"]`;

// Structural, non-authored regions that must never be decorated.
const SKIP_SEL = '.rv-end-spacer, .rv-page-header, .rv-page-footer';

// The elements whose text runs as one unit. A name never crosses one.
const TEXT_BLOCK_SEL = 'p, li, h1, h2, h3, h4, h5, h6, blockquote, td, th, dt, dd, figcaption, caption';

// Stands in for a line or block break in a block's joined text.
const BREAK = '\u0000';

// A word character in any script. What a name may not touch on either side.
const WORD = '[\\p{L}\\p{N}\\p{M}_]';

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Build one regex that finds every element name, or null when there is nothing
 * to find.
 *
 * Longest name first, because alternation takes the first alternative that
 * matches at a position — sorted this way, "New Grail" is tried before "Grail".
 * A name that appears twice on the board (case aside) is matched once, as the
 * first element carrying it.
 *
 * @param {Array<{_id:string, name:string, type:string}>} nodes board nodes
 * @returns {{ regex: RegExp, elements: object[] } | null}
 */
export function buildElementMatcher(nodes) {
  const seen = new Set();
  const elements = [];
  (nodes || []).forEach((n) => {
    if (!n || !TYPES.has(n.type) || n._id == null) return;
    const name = typeof n.name === 'string' ? n.name.trim() : '';
    if (!name) return;
    const key = name.replace(/\s+/g, ' ').toLocaleLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    elements.push({ node: n, name });
  });
  if (elements.length === 0) return null;

  // Stable sort: equal lengths keep board order.
  elements.sort((a, b) => b.name.length - a.name.length);
  const alternatives = elements
    .map((e) => `(${e.name.split(/\s+/).map(escapeRegExp).join('\\s+')})`)
    .join('|');
  const regex = new RegExp(`(?<!${WORD})(?:${alternatives})(?!${WORD})`, 'giu');
  return { regex, elements: elements.map((e) => e.node) };
}

/**
 * Every element name in a string, as non-overlapping [start, end) ranges.
 *
 * @param {string} text
 * @param {{ regex: RegExp, elements: object[] } | null} matcher from buildElementMatcher
 * @returns {Array<{ start:number, end:number, node:object }>}
 */
export function findElementMatches(text, matcher) {
  if (!matcher || !text) return [];
  const { regex, elements } = matcher;
  const found = [];
  regex.lastIndex = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    // Exactly one group took part; its index says which element matched.
    const i = m.findIndex((g, idx) => idx > 0 && g !== undefined);
    found.push({ start: m.index, end: m.index + m[0].length, node: elements[i - 1] });
  }
  return found;
}

/**
 * Remove every element highlight from the live DOM, restoring original content.
 * Idempotent and lossless. Safe to call when nothing is highlighted.
 * @param {Element|null} root editor host
 */
export function clearElementHighlights(root) {
  if (!root) return;
  root.querySelectorAll(SELECTOR).forEach((span) => {
    const parent = span.parentNode;
    if (!parent) return;
    // NOTE: deliberately no normalize(), for the reason clearCommentHighlights
    // gives — a live caret anchored to a sibling text node stays valid.
    while (span.firstChild) parent.insertBefore(span.firstChild, span);
    parent.removeChild(span);
  });
}

/**
 * (Re)build all element highlights from scratch. Always clears first, so it is
 * idempotent — repeated calls never accumulate or double-wrap.
 *
 * @param {Element|null} root        editor host
 * @param {Array}        nodes       the Story Elements board's nodes
 * @param {Element|null} activeBlock block holding the caret
 */
export function applyElementHighlights(root, nodes, activeBlock = null) {
  if (!root) return;

  const caretOffset = activeBlock ? caretOffsetIn(activeBlock) : null;
  let activeChanged = !!(activeBlock && activeBlock.querySelector(SELECTOR));

  clearElementHighlights(root);

  const matcher = buildElementMatcher(nodes);
  if (matcher) {
    // Collect text nodes and <br>s first (never mutate while the walker walks).
    // Every other element is walked through, not collected.
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
      acceptNode(n) {
        if (n.nodeType === 1) {
          if (n.matches(SKIP_SEL)) return NodeFilter.FILTER_REJECT;
          return n.tagName === 'BR' ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
        }
        if (!n.nodeValue) return NodeFilter.FILTER_REJECT;
        const parent = n.parentElement;
        if (!parent) return NodeFilter.FILTER_REJECT;
        if (parent.closest(`${SKIP_SEL}, ${SELECTOR}`)) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    // Grouped by the NEAREST text block, so a name is found across
    // per-character spans but never across two paragraphs, two list items or
    // two table cells. (commentHighlights groups by page-body child first,
    // which joins a whole list into one string — "Craig" and "Zoë" in two
    // items read as "CraigZoë" and neither is found.) Within a block, a <br>
    // or another block's text in between becomes a break in the joined text.
    const groups = new Map(); // block -> { items: (Text|null)[], last: number }
    let seq = 0;
    let node;
    while ((node = walker.nextNode())) {
      const el = node.nodeType === 1 ? node : node.parentElement;
      const block = el.closest(TEXT_BLOCK_SEL) || el.closest('.rv-page-body > *') || el;
      let group = groups.get(block);
      if (!group) { group = { items: [], last: -1 }; groups.set(block, group); }
      if (node.nodeType === 1 || (group.last !== -1 && group.last !== seq - 1)) group.items.push(null);
      if (node.nodeType === 3) group.items.push(node);
      group.last = seq++;
    }

    let occurrence = 0;
    groups.forEach(({ items }, block) => {
      const wrapped = highlightBlock(items, matcher, () => occurrence++);
      if (wrapped && activeBlock && (block === activeBlock || activeBlock.contains(block)
        || block.contains(activeBlock))) {
        activeChanged = true;
      }
    });
  }

  // Wrapping replaced the text nodes the caret lived in; put it back — but only
  // if its block was touched, so a pass that changed nothing near the caret
  // leaves the selection (and any IME state riding on it) alone.
  if (activeChanged && caretOffset != null && activeBlock && root.contains(activeBlock)) {
    restoreCaretIn(activeBlock, caretOffset);
  }
}

/**
 * Find and wrap every element name within ONE block.
 *
 * @param {Array<Text|null>} items the block's text nodes in document order,
 *   with null wherever the text breaks (a <br>, a nested block)
 * @param {object} matcher from buildElementMatcher
 * @param {() => number} nextOccurrence hands out a fresh occurrence id
 * @returns {boolean} whether anything was wrapped
 */
function highlightBlock(items, matcher, nextOccurrence) {
  let joined = '';
  const layout = [];
  items.forEach((n) => {
    // A break is a character no name can match through or touch: not a word
    // character (so it is a boundary) and not whitespace (so "New Grail" does
    // not span a line break). It belongs to no node, so nothing wraps it.
    if (n === null) { joined += BREAK; return; }
    const start = joined.length;
    joined += n.nodeValue;
    layout.push({ node: n, start, end: joined.length });
  });

  const matches = findElementMatches(joined, matcher);
  if (matches.length === 0) return false;

  // Project each block-level match onto the nodes it covers.
  const perNode = new Map();
  matches.forEach((m) => {
    const occurrence = nextOccurrence();
    layout.forEach((seg) => {
      const from = Math.max(m.start, seg.start);
      const to = Math.min(m.end, seg.end);
      if (to <= from) return;
      if (!perNode.has(seg.node)) perNode.set(seg.node, []);
      perNode.get(seg.node).push({
        start: from - seg.start, end: to - seg.start, node: m.node, occurrence,
      });
    });
  });

  // Safe to mutate now: every range was computed before any node changed.
  perNode.forEach((ranges, textNode) => wrapRanges(textNode, ranges));
  return true;
}

/**
 * Replace one text node with its text, wrapping the given local ranges.
 * @param {Text} textNode
 * @param {Array<{start:number, end:number, node:object, occurrence:number}>} ranges
 */
function wrapRanges(textNode, ranges) {
  const text = textNode.nodeValue;
  ranges.sort((a, b) => a.start - b.start);

  const frag = document.createDocumentFragment();
  let cursor = 0;
  ranges.forEach(({ start, end, node, occurrence }) => {
    if (start > cursor) frag.appendChild(document.createTextNode(text.slice(cursor, start)));
    const span = document.createElement('span');
    span.setAttribute(HIGHLIGHT_ATTR, KIND);
    span.setAttribute(ELEMENT_ID_ATTR, String(node._id));
    span.setAttribute(ELEMENT_MATCH_ATTR, String(occurrence));
    span.className = `${HL_CLASS} ${HL_CLASS}--${node.type}`;
    span.appendChild(document.createTextNode(text.slice(start, end)));
    frag.appendChild(span);
    cursor = end;
  });
  if (cursor < text.length) frag.appendChild(document.createTextNode(text.slice(cursor)));

  textNode.replaceWith(frag);
}
